import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { SITE } from "../../data/site";

const NAV = [
  { to: "/",              label: "Home" },
  { to: "/our-craft",     label: "Our Craft" },
  { to: "/products",      label: "Products" },
  { to: "/custom-orders", label: "Custom Orders" },
  { to: "/projects",      label: "Projects" },
  { to: "/about",         label: "About" },
  { to: "/contact",       label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-3 left-3 right-3 md:top-4 md:left-6 md:right-6 z-40 transition-all duration-500 ${
        scrolled ? "glass shadow-soft rounded-full" : "glass rounded-full"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-5 h-14 md:h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 group">
          <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#1e2a5a] flex items-center justify-center overflow-hidden shadow-pill">
            <img src={SITE.logoUrl} alt="Indus Art" className="w-full h-full object-cover" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-lg text-[#1e2a5a] font-medium">Indus Art</div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-[#8a9a7b] -mt-0.5">Art &amp; Emporium</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                  isActive
                    ? "bg-[#1e2a5a]/10 text-[#1e2a5a]"
                    : "text-[#1a1a1a]/75 hover:text-[#1e2a5a] hover:bg-[#1e2a5a]/5"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 rounded-full bg-[#1e2a5a]/10 flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} strokeWidth={2} className="text-[#1e2a5a]" /> : <Menu size={20} strokeWidth={2} className="text-[#1e2a5a]" />}
          </button>
        </div>
      </div>

      {/* Mobile overlay - solid background for readability */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-x-3 top-20 rounded-[2rem] bg-[#faf6ef] shadow-soft border border-[#1a1a1a]/10 transition-all duration-500 overflow-hidden ${
          open ? "opacity-100 visible max-h-[80vh]" : "opacity-0 invisible max-h-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col p-6 gap-1">
          {NAV.map((n, i) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={{ transitionDelay: `${i * 30}ms` }}
              className={`font-serif text-2xl text-[#1e2a5a] py-3 px-4 rounded-2xl hover:bg-[#1e2a5a]/5 transition-all duration-500 ${
                open ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/contact#lead-form"
            data-testid="mobile-request-quote"
            className="mt-4 btn-pill bg-[#1e2a5a] text-[#faf6ef] shadow-pill py-3.5"
          >
            Request a Quote <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
        </div>
      </div>
    </header>
  );
}
